import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Sidebar,
  Icon,
  Form,
  Segment,
} from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';
import parseSpreadsheetFile, {
  USER_COLUMN_MAP,
  GROUP_COLUMN_MAP,
} from 'helpers/parseSpreadsheetFile';
import './index.scss';
import {
  creditGroup,
  creditUser,
  creditBulkUsers,
  creditBulkGroups,
} from 'redux/actions/wallets/credit';
import notify from 'helpers/notificationHelper';
import { ERROR } from 'constants/notificationStatus';

import CreditOrganizationForm from 'components/Wallet/Credit/CreditOrganizationForm';
import CreditGroupForm from 'components/Wallet/Credit/CreditGroupForm';
import CreditUserForm from 'components/Wallet/Credit/CreditUserForm';
import {
  CREDIT_SOURCE,
  CREDIT_CHANNEL,
} from 'constants/walletCreditDefaults';

const CreditSidebar = ({
  filterOn,
  setActionType,
  currentOrgId,
  groupId,
  userId,
}) => {
  const [form, setForm] = useState({});
  const [file, setFile] = useState({});
  const dispatch = useDispatch();

  const { loading } = useSelector(
    ({ wallets: { creditAgent } }) => creditAgent,
  );

  const { items: walletItems = [] } = useSelector(
    ({ wallets: { wallets } }) => wallets,
  );

  const handleChange = (name, value) => {
    if (name === 'filter') {
      setForm({ filter: value });
      return;
    }
    if (name === 'bulk') {
      setForm({
        filter: form.filter,
        [name]: value,
      });

      setFile({});

      return;
    }
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async () => {
    if (userId) {
      return creditUser(
        {
          ...form,
          organization_id: currentOrgId,
          source: CREDIT_SOURCE,
          channel: CREDIT_CHANNEL,
        },
        userId,
      )(dispatch);
    }

    if (groupId) {
      return creditGroup(
        {
          ...form,
          organization_id: currentOrgId,
          source: CREDIT_SOURCE,
          channel: CREDIT_CHANNEL,
        },
        groupId,
      )(dispatch);
    }

    if (form.filter === 'user' && !form.bulk) {
      return creditUser(
        {
          ...form,
          organization_id: currentOrgId,
          channel: CREDIT_CHANNEL,
        },
        form.user,
      )(dispatch);
    }

    if (form.filter === 'group' && !form.bulk) {
      return creditGroup(
        {
          ...form,
          organization_id: currentOrgId,
          channel: CREDIT_CHANNEL,
        },
        form.group,
      )(dispatch);
    }

    if (form.filter === 'user' && form.bulk) {
      try {
        const users = await parseSpreadsheetFile(
          file,
          USER_COLUMN_MAP,
        );
        return creditBulkUsers({
          ...form,
          users,
          organization_id: currentOrgId,
          channel: CREDIT_CHANNEL,
        })(dispatch);
      } catch (err) {
        return notify({ message: err.message, status: ERROR });
      }
    }

    if (form.filter === 'group' && form.bulk) {
      try {
        const groups = await parseSpreadsheetFile(
          file,
          GROUP_COLUMN_MAP,
        );
        return creditBulkGroups({
          ...form,
          groups,
          organization_id: currentOrgId,
          channel: CREDIT_CHANNEL,
        })(dispatch);
      } catch (err) {
        return notify({ message: err.message, status: ERROR });
      }
    }

    return null;
  };

  return (
    <Sidebar
      className="sidebar scrollable"
      as={Segment}
      direction="right"
      animation="overlay"
      visible={filterOn}
      width="wide"
    >
      <div className="padding">
        <div className="header">
          <div
            tabIndex="-1"
            role="button"
            onKeyDown={() => null}
            className="back"
            onClick={() => {
              setActionType(null);
            }}
          >
            <Icon name="arrow left" fitted size="large" />
          </div>
          <div className="title titleText">
            <h2>Credit</h2>
          </div>
        </div>
      </div>
      <div className="padding">
        <Form onSubmit={handleSubmit} loading={loading}>
          {userId && (
            <CreditUserForm form={form} onChange={handleChange} />
          )}
          {!userId && groupId && (
            <CreditGroupForm form={form} onChange={handleChange} />
          )}
          {!userId && !groupId && (
            <CreditOrganizationForm
              form={form}
              onChange={handleChange}
              onFileChange={setFile}
              file={file}
              currentOrgId={currentOrgId}
              walletItems={walletItems}
            />
          )}
          <Button
            primary
            className="sidebar__submit-btn"
            floated="right"
            size="medium"
            content="Initiate"
            type="submit"
          />
        </Form>
      </div>
    </Sidebar>
  );
};

CreditSidebar.defaultProps = {
  groupId: null,
  userId: null,
};

CreditSidebar.propTypes = {
  filterOn: PropTypes.bool.isRequired,
  setActionType: PropTypes.func.isRequired,
  currentOrgId: PropTypes.number.isRequired,
  groupId: PropTypes.number,
  userId: PropTypes.number,
};

export default CreditSidebar;
